// Appraisal Theme Main JavaScript (Modular)
// Import Bootstrap and custom functionality

import 'bootstrap';
import '../scss/main.scss';

// Import modules
import { calendarManager } from './modules/calendar.js';
import { appointmentManager } from './modules/appointments.js';
import { availabilityManager } from './modules/availability.js';
import { formManager } from './modules/forms.js';
import { notificationManager } from './modules/notifications.js';

// Main theme class
class AppraisalTheme {
    constructor() {
        this.modules = {
            calendar: calendarManager,
            appointments: appointmentManager,
            availability: availabilityManager,
            forms: formManager,
            notifications: notificationManager
        };
        
        this.init();
    }

    // Initialize theme
    init() {
        
        // Initialize logout functionality
        this.initLogout();
        document.addEventListener('DOMContentLoaded', () => {
            this.initializeTheme();
        });
    }

    // Initialize logout functionality
    initLogout() {
        const logoutBtn = document.getElementById('logout-btn');
        if (logoutBtn) {
            logoutBtn.addEventListener('click', (e) => {
                // Don't prevent default - let the link work normally
                
                // Add a small delay to ensure the click is registered
                setTimeout(() => {
                    // If the page hasn't redirected after 2 seconds, try alternative logout
                    if (window.location.href === logoutBtn.href) {
                        // Try direct redirect to logout URL
                        window.location.href = logoutBtn.href;
                    }
                }, 2000);
            });
        }
    }

    // Initialize theme functionality
    initializeTheme() {
        
        // Initialize all modules
        this.initializeModules();
        
        // Initialize page-specific functionality
        this.initializePageSpecific();
    }

    // Initialize all modules
    initializeModules() {
        Object.keys(this.modules).forEach(moduleName => {
            const module = this.modules[moduleName];
            if (module && typeof module.init === 'function') {
                try {
                    module.init();
                } catch (error) {
                    console.error(`Appraisal Theme: Error initializing ${moduleName} module:`, error);
                }
            }
        });
    }

    // Initialize page-specific functionality
    initializePageSpecific() {
        const body = document.body;
        const pageClass = body.className;
        
        // Initialize calendar on pages that need it
        if (pageClass.includes('appraiser-availability') || 
            pageClass.includes('page-template-templates-appraiser-availability') ||
            pageClass.includes('member-pick-appointment') ||
            pageClass.includes('page-template-templates-member-pick-appointment') ||
            pageClass.includes('appraiser-dashboard') ||
            window.location.pathname.includes('/appraiser-availability/')) {
            if (this.modules.calendar && typeof this.modules.calendar.initCalendar === 'function') {
                this.modules.calendar.initCalendar();
            }
        }
        
        // Initialize appointment management
        if (pageClass.includes('page-template-templates-appraiser-appointments') || 
            pageClass.includes('page-template-templates-member-appointments')) {
            if (this.modules.appointments && typeof this.modules.appointments.init === 'function') {
                this.modules.appointments.init();
            }
        }
        
        // Initialize availability management
        if (pageClass.includes('page-template-templates-appraiser-availability') || 
            pageClass.includes('appraiser-availability') ||
            window.location.pathname.includes('/appraiser-availability/')) {
            if (this.modules.availability && typeof this.modules.availability.init === 'function') {
                this.modules.availability.init();
            }
        }
        
        // Initialize forms
        if (this.modules.forms && typeof this.modules.forms.init === 'function') {
            this.modules.forms.init();
        }
        
        // Initialize notifications
        if (this.modules.notifications && typeof this.modules.notifications.init === 'function') {
            this.modules.notifications.init();
        }
    }
}

// Initialize the theme when the script loads
const appraisalTheme = new AppraisalTheme();

// Add global click handler for booking button as fallback
document.addEventListener('click', (e) => {
    if (e.target && e.target.id === 'book-appointment-btn') {
        const form = document.getElementById('booking-form');
        if (form && window.appraisalTheme && window.appraisalTheme.modules.forms) {
            e.preventDefault();
            window.appraisalTheme.modules.forms.handleBookingFormSubmit(form);
        }
    }
});

// Expose functions globally for modal interactions
window.appraisalTheme = appraisalTheme;

// Expose module managers globally for access
window.appraisalTheme.calendarManager = appraisalTheme.modules.calendar;
window.appraisalTheme.appointmentManager = appraisalTheme.modules.appointments;
window.appraisalTheme.availabilityManager = appraisalTheme.modules.availability;
window.appraisalTheme.notificationManager = appraisalTheme.modules.notifications;

// Expose notification methods
window.appraisalTheme.showNotification = (message, type = 'info') => {
    if (appraisalTheme.modules.notifications) {
        appraisalTheme.modules.notifications.show(message, type);
    }
};

// Expose module functions globally for backward compatibility
window.appraisalTheme.deleteAvailability = (availabilityId) => {
    if (appraisalTheme.modules.availability) {
        appraisalTheme.modules.availability.deleteAvailability(availabilityId);
    }
};

// Expose functions globally for onclick handlers
window.deleteAvailability = (availabilityId) => {
    if (window.appraisalTheme && window.appraisalTheme.modules.availability) {
        window.appraisalTheme.modules.availability.deleteAvailability(availabilityId);
    }
};

window.appraisalTheme.deleteAppointment = (appointmentId) => {
    if (window.appraisalTheme.modules.appointments) {
        window.appraisalTheme.modules.appointments.deleteAppointment(appointmentId);
    }
};

window.deleteAppointment = (appointmentId) => {
    if (window.appraisalTheme && window.appraisalTheme.modules.appointments) {
        window.appraisalTheme.modules.appointments.deleteAppointment(appointmentId);
    }
};

window.updateAppointmentStatus = (appointmentId, status) => {
    if (window.appraisalTheme && window.appraisalTheme.modules.appointments) {
        window.appraisalTheme.modules.appointments.updateAppointmentStatus(appointmentId, status);
    }
};

// Export for potential external use
export default appraisalTheme;